import type { FC, RefObject } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";

import { useUser } from "@ui/hooks/useUser";
import { useIsAuthPage } from "@ui/providers/AuthRoutingProvider";

import { useConfig } from "@jti/app-context/useConfig";
import { getCookie, isServerSide } from "@jti/fe-common";
import { CasAuthApi } from "@jti/rest-api";

import { useLogOut } from "../../hooks/useLogOut";

import type { UserIndicationButtonProps } from "./components/UserIndicationButton/UserIndicationButton";
import { UserIndicationButton } from "./components/UserIndicationButton/UserIndicationButton";

export type UserActionLink = {
  title: string;
  href: string;
};

export type UserLoggedWithPending = "logged" | "notLogged" | "pending";

interface UserActionsProps extends UserIndicationButtonProps {
  tabletUserNameRef?: RefObject<HTMLDivElement | null>;
}

export const UserActions: FC<UserActionsProps> = ({
  tabletUserNameRef,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  ...props
}) => {
  const config = useConfig();
  const currentPathname = !isServerSide() ? window.location.pathname : "";
  const isAuthPage = useIsAuthPage(currentPathname);
  const [userLoggedState, setUserLoggedState] = useState<UserLoggedWithPending>("pending");
  const { user } = useUser();
  const onLogOut = useLogOut(props.onLogOut);

  useEffect(() => {
    const validateUserSession = async () => {
      if (isAuthPage) {
        setUserLoggedState("notLogged");
        return;
      }

      if (user?.ciam_token) {
        try {
          const { statusCode } = await CasAuthApi.init(config).validate(user.ciam_token, true);
          const isValidSession = statusCode === 200 || statusCode === 204;
          const loggedState = isValidSession ? "logged" : "notLogged";

          if (!isValidSession) {
            onLogOut();
          }
          setUserLoggedState(loggedState);
        } catch {
          setUserLoggedState("notLogged");
          onLogOut();
        }
      } else {
        if (getCookie("isUserLoggedIn") === "true") {
          onLogOut();
        }
        setUserLoggedState("notLogged");
      }
    };

    if (user) {
      validateUserSession();
    }
  }, [user, isAuthPage]);

  const MEDIUM_WIDTH = 1280;
  const SMALL_WIDTH = 960;

  const isBelowDesktopBreakpoint = useMediaQuery({
    query: `(min-width: ${SMALL_WIDTH}px) and (max-width: ${MEDIUM_WIDTH - 1}px)`,
  });

  const isUserLoggedIn = userLoggedState === "logged";

  const userActionElement = (
    <UserIndicationButton
      isEnabledUserIndication={isEnabledUserIndication}
      isEnabledUserNameForIndication={isEnabledUserNameForIndication}
      isUserLoggedIn={isUserLoggedIn}
      isUserPending={userLoggedState === "pending"}
      user={user}
      isAuthPage={isAuthPage}
      {...props}
    />
  );

  if (isUserLoggedIn && isEnabledUserIndication && isEnabledUserNameForIndication && isBelowDesktopBreakpoint) {
    if (tabletUserNameRef?.current) {
      return createPortal(userActionElement, tabletUserNameRef.current);
    }
  }

  return userActionElement;
};
